/**
 * WorkflowTimeline — horizontal day-by-day stepper that visualises the
 * AI Risk Essentials 5-step / 7-day workflow.
 *
 * Renders a connected timeline (rail + nodes) where the active step is
 * highlighted with the platform gold, completed steps carry a check mark,
 * and upcoming steps fade to slate.  Tooltips reveal each step's summary
 * on hover for context without cluttering the page.
 */
import React from 'react';
import { Check, Loader2 } from 'lucide-react';

/**
 * @param {Object[]} steps   — [{step, days_label, title, summary}]
 * @param {number}   active  — currently active step (1-based)
 * @param {boolean}  loading — show spinner on the active node
 */
export default function WorkflowTimeline({ steps = [], active = 1, loading = false }) {
  if (!steps.length) return null;
  return (
    <div className="ve-card p-5 md:p-6" data-testid="erm-workflow-timeline">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
        <div>
          <div className="ve-eyebrow text-[#5E1F8B]">The AI Risk Essentials Workflow</div>
          <h3 className="display text-lg font-bold mt-1">
            From a blank spreadsheet to a board-ready heatmap{' '}
            <span className="ve-gold-text">in 7 days.</span>
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
          Step {active} of {steps.length}
        </div>
      </div>

      {/* Rail */}
      <div className="relative">
        {/* Horizontal connector — sits behind the nodes */}
        <div className="absolute top-4 left-4 right-4 h-[2px] bg-slate-200 hidden md:block" />
        <div
          className="absolute top-4 left-4 h-[2px] bg-[#8B3FC5] hidden md:block transition-all duration-500"
          style={{ width: `calc(${((active - 1) / Math.max(1, steps.length - 1)) * 100}% - 1rem)` }}
        />

        <ol className="relative grid grid-cols-1 md:grid-cols-5 gap-5 md:gap-2">
          {steps.map((s) => {
            const isDone = s.step < active;
            const isActive = s.step === active;
            return (
              <li
                key={s.step}
                data-testid={`erm-workflow-step-${s.step}`}
                data-active={isActive ? 'true' : 'false'}
                className="relative flex flex-col md:items-center md:text-center group"
              >
                <div
                  className={`h-8 w-8 rounded-full border-2 flex items-center justify-center font-bold text-xs shrink-0 transition-all relative z-10 ${
                    isDone
                      ? 'bg-[#8B3FC5] border-[#8B3FC5] text-white'
                      : isActive
                        ? 'border-[#8B3FC5] text-[#5E1F8B] bg-[#FFF8E5] shadow-[0_0_18px_-4px_rgba(174,82,234,0.6)]'
                        : 'border-slate-200 text-slate-400 bg-white'
                  }`}
                >
                  {isDone ? <Check size={14} />
                    : isActive && loading ? <Loader2 size={14} className="animate-spin" />
                      : s.step}
                </div>
                <div className="mt-3 md:px-1 max-w-[12rem]">
                  <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${
                    isActive ? 'text-[#5E1F8B]' : isDone ? 'text-slate-700' : 'text-slate-400'
                  }`}>
                    {s.days_label}
                  </div>
                  <div className={`mt-1 text-sm font-bold leading-tight ${
                    isActive ? 'text-slate-900' : isDone ? 'text-slate-700' : 'text-slate-500'
                  }`}>
                    {s.title}
                  </div>
                  <p className="hidden md:block text-[11px] leading-snug text-slate-500 mt-1">
                    {s.summary}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
